import apiClient from './axiosBase'

/**
 * jizaipad-api の認証 API（`/v1/auth/*`）。
 *
 * 認証途中の状態（MFA 待ち・初回パスワード登録待ち）とログイン後のセッションは、すべて api が
 * 発行する HttpOnly Cookie（`api_auth_attempt` / `api_password_registration` / `api_session`）で
 * 運ばれる。クライアントは識別子を保持せず、`status` だけを見て画面を進める。
 *
 * - Cookie 送出（`withCredentials`）と `X-CSRF-Token` の付与は apiClient が行う。
 *   ログイン前でも `GET /v1/sessions` が 204 で CSRF Cookie を返すため、POST の直前に
 *   request interceptor が取り直す（production では CSRF 無しの POST が 500 になる）。
 * - エラーは axios のエラーをそのまま投げる。文言への写しは `shared/auth/logic` が行う。
 */

/** ログイン・初回パスワード登録の結果。 */
export type LoginStatus = 'authenticated' | 'mfaRequired' | 'passwordRegistrationRequired'

interface LoginResult {
  status: LoginStatus
}

/** 初回パスワード登録の結果。`passwordRegistrationRequired` は返らない。 */
export type PasswordRegistrationStatus = Exclude<LoginStatus, 'passwordRegistrationRequired'>

/**
 * reCAPTCHA のトークン。ログインとパスワード再設定の開始・確定の 3 経路だけに付ける。
 * 取得できなかったときは undefined のまま渡し、キーごと送らない（api は当面欠落を受理する）。
 */
interface RecaptchaParams {
  recaptchaToken?: string
}

/** パスワードでログインする。 */
export async function postLogin (params: { email: string, password: string } & RecaptchaParams): Promise<LoginStatus> {
  const response = await apiClient.post<LoginResult>('/v1/auth/login', params)
  return response.data.status
}

/** メールで届いた MFA コードを確認する。成功すると `api_session` が発行される。 */
export async function postMfaVerification (code: string): Promise<void> {
  await apiClient.post('/v1/auth/mfa/verifications', { code })
}

/** MFA コードを再送する（60 秒間隔・3 回まで。超過は 429）。 */
export async function postMfaCodeResend (): Promise<void> {
  await apiClient.post('/v1/auth/mfa/codes')
}

/** 仮パスワードで招待されたユーザーの初回パスワードを登録する。 */
export async function postPasswordRegistration (newPassword: string): Promise<PasswordRegistrationStatus> {
  const response = await apiClient.post<{ status: PasswordRegistrationStatus }>(
    '/v1/auth/password_registrations',
    { newPassword }
  )
  return response.data.status
}

/**
 * ログイン中ユーザーのパスワードを変更する。
 * 成功すると api は**そのユーザーの全 API セッション（変更元を含む）を失効する**ため、
 * 呼び出し側はログインし直させる必要がある。
 */
export async function putPassword (params: { currentPassword: string, newPassword: string }): Promise<void> {
  await apiClient.put('/v1/auth/password', params)
}

/**
 * パスワード再設定コード（10 進 6 桁、有効 1 時間）をメールで送らせる。
 * 存在しないメールアドレスでも同じ 204 が返る（列挙防止）。
 */
export async function postPasswordReset (params: { email: string } & RecaptchaParams): Promise<void> {
  await apiClient.post('/v1/auth/password_resets', params)
}

/** 再設定コードと新しいパスワードでパスワードを再設定する。セッションは発行されない。 */
export async function postPasswordResetConfirmation (params: {
  email: string
  code: string
  newPassword: string
} & RecaptchaParams): Promise<void> {
  await apiClient.post('/v1/auth/password_resets/confirmation', params)
}

/**
 * ログアウトする（`api_session` を失効し Cookie を削除する）。
 * `api_session` が無い・無効でも 204 を返す（CSRF も不要）。
 */
export async function deleteAuthSession (): Promise<void> {
  await apiClient.delete('/v1/auth/session')
}
